import { FC, useState, useEffect, useRef } from 'react';
import { DatesRangeValue } from '@mantine/dates';
import { TabletFilterDateInput } from './shared/TabletFilterDateInput';
import { TabletFilterSection } from './shared/TabletFilterSection';

export interface DateFilterProps {
  startDateStart?: Date;
  startDateEnd?: Date;
  endDateStart?: Date;
  endDateEnd?: Date;
  creationDateStart?: Date;
  creationDateEnd?: Date;
  paidDateStart?: Date;
  paidDateEnd?: Date;
  onChange: (dates: {
    startDateStart?: Date;
    startDateEnd?: Date;
    endDateStart?: Date;
    endDateEnd?: Date;
    creationDateStart?: Date;
    creationDateEnd?: Date;
    paidDateStart?: Date;
    paidDateEnd?: Date;
  }) => void;
  onReset: () => void;
  onApply: (dates: {
    startDateStart?: Date;
    startDateEnd?: Date;
    endDateStart?: Date;
    endDateEnd?: Date;
    creationDateStart?: Date;
    creationDateEnd?: Date;
    paidDateStart?: Date;
    paidDateEnd?: Date;
  }) => void;
}

export const TabletDateFilter: FC<DateFilterProps> = ({
  startDateStart,
  startDateEnd,
  endDateStart,
  endDateEnd,
  creationDateStart,
  creationDateEnd,
  onChange,
  paidDateStart,
  paidDateEnd,
}) => {
  const [localStartRange, setLocalStartRange] = useState<DatesRangeValue>([
    startDateStart || null,
    startDateEnd || null,
  ]);
  const [localEndRange, setLocalEndRange] = useState<DatesRangeValue>([
    endDateStart || null,
    endDateEnd || null,
  ]);
  const [localPaidRange, setLocalPaidRange] = useState<DatesRangeValue>([
    paidDateStart || null,
    paidDateEnd || null,
  ]);
  const [localCreationRange, setLocalCreationRange] = useState<DatesRangeValue>([
    creationDateStart || null,
    creationDateEnd || null,
  ]);

  const startRangeRef = useRef(localStartRange);
  const endRangeRef = useRef(localEndRange);
  const creationRangeRef = useRef(localCreationRange);
  const paidRangeRef = useRef(localPaidRange);

  useEffect(() => {
    startRangeRef.current = localStartRange;
  }, [localStartRange]);

  useEffect(() => {
    endRangeRef.current = localEndRange;
  }, [localEndRange]);

  useEffect(() => {
    creationRangeRef.current = localCreationRange;
  }, [localCreationRange]);

  useEffect(() => {
    setLocalStartRange([startDateStart || null, startDateEnd || null]);
    setLocalEndRange([endDateStart || null, endDateEnd || null]);
    setLocalCreationRange([creationDateStart || null, creationDateEnd || null]);
    setLocalPaidRange([paidDateStart || null, paidDateEnd || null]);
  }, [
    startDateStart,
    startDateEnd,
    endDateStart,
    endDateEnd,
    creationDateStart,
    creationDateEnd,
    paidDateStart,
    paidDateEnd,
  ]);

  const updateParent = (
    newStart?: DatesRangeValue,
    newEnd?: DatesRangeValue,
    newCreation?: DatesRangeValue,
    newPaid?: DatesRangeValue,
  ) => {
    onChange({
      startDateStart: (newStart || startRangeRef.current)[0] || undefined,
      startDateEnd: (newStart || startRangeRef.current)[1] || undefined,
      endDateStart: (newEnd || endRangeRef.current)[0] || undefined,
      endDateEnd: (newEnd || endRangeRef.current)[1] || undefined,
      creationDateStart: (newCreation || creationRangeRef.current)[0] || undefined,
      creationDateEnd: (newCreation || creationRangeRef.current)[1] || undefined,
      paidDateStart: (newPaid || paidRangeRef.current)[0] || undefined,
      paidDateEnd: (newPaid || paidRangeRef.current)[1] || undefined,
    });
  };

  const handleStartRangeChange = (value: unknown) => {
    let newRange: DatesRangeValue = [null, null];
    if (Array.isArray(value) && value.length >= 2) {
      newRange = [value[0], value[1]];
    } else if (Array.isArray(value) && value.length === 1) {
      newRange = [value[0], value[0]];
    } else if (value) {
      newRange = [value as Date, value as Date];
    }

    setLocalStartRange(newRange);
    startRangeRef.current = newRange;
    updateParent(newRange);
  };

  const handleEndRangeChange = (value: unknown) => {
    let newRange: DatesRangeValue = [null, null];
    if (Array.isArray(value) && value.length >= 2) {
      newRange = [value[0], value[1]];
    } else if (Array.isArray(value) && value.length === 1) {
      newRange = [value[0], value[0]];
    } else if (value) {
      newRange = [value as Date, value as Date];
    }

    setLocalEndRange(newRange);
    endRangeRef.current = newRange;
    updateParent(undefined, newRange);
  };

  const handleCreationRangeChange = (value: unknown) => {
    let newRange: DatesRangeValue = [null, null];
    if (Array.isArray(value) && value.length >= 2) {
      newRange = [value[0], value[1]];
    } else if (Array.isArray(value) && value.length === 1) {
      newRange = [value[0], value[0]];
    } else if (value) {
      newRange = [value as Date, value as Date];
    }

    setLocalCreationRange(newRange);
    creationRangeRef.current = newRange;
    updateParent(undefined, undefined, newRange);
  };

  const handlePaidRangeChange = (value: unknown) => {
    let newRange: DatesRangeValue = [null, null];
    if (Array.isArray(value) && value.length >= 2) {
      newRange = [value[0], value[1]];
    } else if (Array.isArray(value) && value.length === 1) {
      newRange = [value[0], value[0]];
    } else if (value) {
      newRange = [value as Date, value as Date];
    }

    setLocalPaidRange(newRange);
    paidRangeRef.current = newRange;
    updateParent(undefined, undefined, undefined, newRange);
  };

  return (
    <div>
      <TabletFilterSection variant='field'>
        <div style={{ color: '#71869D', fontSize: '14px' }}>Дата начала</div>
        <TabletFilterDateInput
          type='range'
          value={localStartRange}
          onChange={handleStartRangeChange}
          placeholder='Выбери период'
        />
      </TabletFilterSection>

      <TabletFilterSection variant='field'>
        <div style={{ color: '#71869D', fontSize: '14px' }}>Дата завершения</div>
        <TabletFilterDateInput
          type='range'
          value={localEndRange}
          onChange={handleEndRangeChange}
          placeholder='Выбери период'
        />
      </TabletFilterSection>

      <TabletFilterSection variant='field'>
        <div style={{ color: '#71869D', fontSize: '14px' }}>Дата создания</div>
        <TabletFilterDateInput
          type='range'
          value={localCreationRange}
          onChange={handleCreationRangeChange}
          placeholder='Выбери период'
        />
      </TabletFilterSection>

      <TabletFilterSection variant='field'>
        <div style={{ color: '#71869D', fontSize: '14px' }}>Дата оплаты</div>
        <TabletFilterDateInput
          type='range'
          value={localPaidRange}
          onChange={handlePaidRangeChange}
          placeholder='Выбери период'
        />
      </TabletFilterSection>
    </div>
  );
};
